import {Component, OnInit, ViewChild} from '@angular/core';
import {combineLatest, merge, Observable, of, Subject} from 'rxjs';
import {UUID} from '@util/types';
import {map, share, switchMap, tap} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {ProductViewComponent} from '@app/product-view/product-view.component';
import {VoilaUserService} from '@services/voila-user.service';
import {VoteService} from '@services/vote.service';
import {Vote} from '@services/model/vote';
import {QueryAndIndex, SearchproductService} from '@services/searchproduct.service';
import {User} from '@services/model/user';
import {Product} from '@services/model/product';
import {ProductService} from '@services/product.service';
import {StartupService} from '@services/startup.service';
import {ProductNavComponent} from '@app/product-view/product-nav/product-nav.component';
import {CommonModule} from '@angular/common';
import {AmpelComponent} from '@hp-dashboard/hp-product-view/ampel/ampel.component';
import {OrderSampleButtonComponent} from '@app/product-view/order-sample-button/order-sample-button.component';
import {SingletonPipe} from '@util/pipes/singleton-pipe';

@Component({
  selector: 'app-hp-product-view',
  templateUrl: './hp-product-view.component.html',
  styleUrls: ['./hp-product-view.component.scss'],
  standalone: true,
  imports: [CommonModule, ProductNavComponent, AmpelComponent, ProductViewComponent, OrderSampleButtonComponent, SingletonPipe],
})
export class HpProductViewComponent implements OnInit {
  favoriteProduct$: Observable<boolean>;
  product$: Observable<Product>;
  productId$: Observable<UUID>;
  cycleId$: Observable<UUID | null>;

  initiationDataLoaded = false;

  queryAndIndex$: Observable<QueryAndIndex>;
  vote$: Observable<Vote | null>;
  updatedVote$: Subject<Vote> = new Subject();

  @ViewChild(ProductViewComponent, {static: true})
  productViewComponent: ProductViewComponent;

  constructor(
    private readonly userService: VoilaUserService,
    private readonly productService: ProductService,
    private readonly route: ActivatedRoute,
    private readonly voteService: VoteService,
    private readonly startupService: StartupService,
  ) {}

  ngOnInit() {
    this.productId$ = this.route.paramMap.pipe(
      map((pm) => pm.get('productId') as string),
      tap(() => (this.initiationDataLoaded = false)),
    );
    this.queryAndIndex$ = this.route.queryParamMap.pipe(SearchproductService.extractQueryAndIndex());
    this.product$ = this.productId$.pipe(
      switchMap((pid) => this.productService.getProduct(pid)),
      share(),
    );

    this.cycleId$ = this.queryAndIndex$.pipe(
      map((qi) => {
        const cycleFilter = qi.query.cycleFilter;
        if (!cycleFilter || cycleFilter === 'all' || cycleFilter === 'none') {
          return null;
        }
        return cycleFilter;
      }),
    );

    const serverVote$ = combineLatest([this.productId$, this.cycleId$]).pipe(
      switchMap((x: [UUID, UUID | null]) => {
        const productId = x[0];
        const cycleId = x[1];
        const companyId = this.userService.currentUser().companyId;

        if (!cycleId) {
          return of(null);
        }

        return this.voteService.getVotes(cycleId, productId, companyId).pipe(map((votes) => (votes[0] && votes[0].vote) || null)) as Observable<Vote | null>;
      }),
      share(),
    );
    this.vote$ = merge(serverVote$, this.updatedVote$);
    this.getFavoriteProductStatus();
  }

  get hasVotingPermissions(): boolean {
    return User.hasVotingPermission(this.userService.currentUser());
  }

  toggleVote(cycleId: UUID, productId: UUID, verdict: number) {
    const companyId = this.userService.currentUser().companyId;
    const newVerdict = verdict === 1 ? 0 : 1;
    this.voteService.castVote(cycleId, productId, companyId, newVerdict).subscribe((v) => this.updatedVote$.next(v));
  }

  setFavoriteProduct(productId: UUID) {
    this.startupService.setFavoriteProduct(this.userService.currentUser().id, productId).subscribe((result) => {
      this.getFavoriteProductStatus();
    });
  }

  deleteFavoriteProduct(productId: UUID) {
    this.startupService.deleteFavoriteProduct(this.userService.currentUser().id, productId).subscribe((result) => {
      this.getFavoriteProductStatus();
    });
  }

  private getFavoriteProductStatus() {
    this.productId$.subscribe((id) => {
      this.favoriteProduct$ = this.startupService.getFavoriteProductStatus(this.userService.currentUser().id, id);
    });
  }
}
